/**
 * A deliberately harsh, synthesized alert tone for urgent proactive alerts.
 *
 * Browsers give web apps no way to customize the OS push-notification sound
 * (`apps/web/public/sw.js` always rings the platform's own chime) — this is
 * the only lever available, and it only works while a Zunelo tab is open:
 * three quick square-wave beeps, deliberately more jarring than a normal UI
 * chime so an urgent alert reads as different from routine app noise.
 */

export interface AlertToneStep {
  freq: number;
  type: OscillatorType;
  startMs: number;
  durationMs: number;
  gain: number;
}

/** Exported for tests: two sharp high tones bracketing a lower one. */
export const ALERT_TONE_PLAN: readonly AlertToneStep[] = [
  { freq: 1046, type: 'square', startMs: 0, durationMs: 90, gain: 0.16 },
  { freq: 622, type: 'square', startMs: 110, durationMs: 90, gain: 0.16 },
  { freq: 1046, type: 'square', startMs: 220, durationMs: 150, gain: 0.16 },
];

export interface AudioParamLike {
  setValueAtTime(value: number, time: number): void;
  linearRampToValueAtTime(value: number, time: number): void;
}

export interface OscillatorLike {
  type: OscillatorType;
  frequency: AudioParamLike;
  connect(destination: unknown): unknown;
  start(time?: number): void;
  stop(time?: number): void;
}

export interface GainNodeLike {
  gain: AudioParamLike;
  connect(destination: unknown): unknown;
}

export interface AudioContextLike {
  currentTime: number;
  destination: unknown;
  createOscillator(): OscillatorLike;
  createGain(): GainNodeLike;
  close?(): Promise<void> | void;
}

function defaultAudioContext(): AudioContextLike | undefined {
  if (typeof window === 'undefined') return undefined;
  const Ctor =
    (
      window as unknown as {
        AudioContext?: new () => AudioContextLike;
        webkitAudioContext?: new () => AudioContextLike;
      }
    ).AudioContext ??
    (window as unknown as { webkitAudioContext?: new () => AudioContextLike }).webkitAudioContext;
  return Ctor ? new Ctor() : undefined;
}

/**
 * Plays `ALERT_TONE_PLAN` through `createContext()`. Never throws: a blocked
 * autoplay policy, an unsupported browser, or a closed AudioContext must
 * never break the notification flow — the OS chime already rang regardless.
 */
export function playAlertTone(
  createContext: () => AudioContextLike | undefined = defaultAudioContext,
  plan: readonly AlertToneStep[] = ALERT_TONE_PLAN,
): void {
  try {
    const ctx = createContext();
    if (!ctx) return;
    const start = ctx.currentTime;
    for (const step of plan) {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.type = step.type;
      const stepStart = start + step.startMs / 1000;
      const stepEnd = stepStart + step.durationMs / 1000;
      oscillator.frequency.setValueAtTime(step.freq, stepStart);
      gainNode.gain.setValueAtTime(step.gain, stepStart);
      gainNode.gain.linearRampToValueAtTime(0, stepEnd);
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start(stepStart);
      oscillator.stop(stepEnd);
    }
    const totalMs = Math.max(...plan.map((step) => step.startMs + step.durationMs), 0);
    if (ctx.close) {
      setTimeout(() => {
        void ctx.close?.();
      }, totalMs + 50);
    }
  } catch {
    /* Web Audio unsupported/blocked — the OS notification sound already rang. */
  }
}

export interface ServiceWorkerContainerLike {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

/**
 * Wires the service worker's `zunelo-push` broadcast to `playAlertTone`.
 * Only urgent alerts (mentions, DMs, threads waiting on you, competence hits,
 * review requests) get the tone — routine background sync stays silent.
 */
export function installAlertToneBridge(
  play: () => void = playAlertTone,
  serviceWorkerContainer: ServiceWorkerContainerLike | undefined = typeof navigator === 'undefined'
    ? undefined
    : (navigator.serviceWorker as unknown as ServiceWorkerContainerLike | undefined),
): void {
  if (!serviceWorkerContainer) return;
  serviceWorkerContainer.addEventListener('message', (event) => {
    const data = event.data as { type?: string; urgent?: boolean } | undefined;
    if (data?.type === 'zunelo-push' && data.urgent) play();
  });
}
